
import { Message as ElMessage } from 'element-ui'
import {
  defineComponent,
  getCurrentInstance,
  nextTick,
  reactive,
  ref,
  shallowRef,
} from 'vue'

import NlpCard from '@/views/NLP/components/NlpCard.vue'

import { MessageBox } from 'element-ui'
import {
  batchDeleteFiles,
  uploadFile,
} from '@/api/common/common-api'
import type { UploadFileRes } from '@/api/common/common-api-type'

type ISelectedFile = UploadFileRes
export default defineComponent({
  components: { NlpCard },
  emits: [
    'select-change',
    'start-task',
  ],
  setup (props, { emit }) {
    const elTableRef = shallowRef()
    const uploadedFiles = shallowRef<ISelectedFile[]>([])

    const selectedFiles = shallowRef<ISelectedFile[]>([])

    function toggleRowSelect (
      row: ISelectedFile,
      selected: boolean,
    ) {
      elTableRef.value?.toggleRowSelection(row, selected)
    }

    function onTableSelectChange (e: ISelectedFile[]) {
      if (e.length > 3) {
        selectedFiles.value = e.slice(0, 3)
        e.slice(3).forEach(row => toggleRowSelect(row, false))
      } else {
        selectedFiles.value = e
      }
    }

    function checkIsRowSelectable (row: ISelectedFile) {
      return selectedFiles.value.length < 3 || selectedFiles.value.findIndex(it => it.id === row.id) > -1
    }

    function deleteFiles (delFiles: ISelectedFile[]) {
      const delFileIds = delFiles.map(i => i.id)
      uploadedFiles.value = uploadedFiles.value.filter(i => !delFileIds.includes(i.id))
      uploadedFiles.value.forEach(row => { // 更新 uploadedFiles.value 后导致 el-table rerender, selection 被清空, 需要保持之前的状态
        if (selectedFiles.value.findIndex(it => it.id === row.id) > -1) {
          nextTick(() => {
            toggleRowSelect(row, true)
          })
        }
      })
      batchDeleteFiles(delFileIds)
    }

    function startRead () {
      if (selectedFiles.value.length === 0) {
        ElMessage.warning('请选择文件')
        return
      }
      emit('start-task', selectedFiles.value.map(i => i.id))
    }

    const showReading = ref(false)

    const btnLoading = reactive({
      import: false,
      del: false,
      startTask: false,
    })
    const uploadInputRef = shallowRef<HTMLInputElement>()
    const vm = getCurrentInstance()
    const h = vm?.proxy.$createElement

    function handleImportBtnClick () {
      if (!btnLoading.import) {
        uploadInputRef.value?.click()
      }
    }

    const UPLOAD_FILE_SIZE_LIMIT = 5 // 单位MB
    function handleUploadInputChange () {
      const files = uploadInputRef.value?.files ?? []

      const uploadFailedFiles: {fileName: string; errorMsg: string}[] = []
      btnLoading.import = true

      const validFileList = Array.from(files)
        .filter(file => {
          if (file) {
            if (file.size / 1024 / 1024 > UPLOAD_FILE_SIZE_LIMIT) {
              uploadFailedFiles.push({
                fileName: file.name,
                errorMsg: `文件大小超过${UPLOAD_FILE_SIZE_LIMIT}MB`,
              })
              return false
            } else {
              return true
            }
          } {
            return false
          }
        })
      const fileMap = new Map<File, { id: string }>()

      if (validFileList.length > 0) {
        validFileList.forEach((file, idx) => fileMap.set(file, { id: `file-id-${idx}`}))
        MessageBox({
          title: '正在上传中...',
          message: h?.('p', undefined, [
            // h?.('span', undefined, `正在上传中... `),
            h?.('ol', undefined, validFileList
              .map((file) => h?.(
                'li',
                undefined,
                [
                  h?.('span', undefined, `${file.name}:`),
                  h?.('span', {attrs: { id: fileMap.get(file)?.id } }, `0%`),
                ])))
          ]),
          type: 'info',
          showCancelButton: false,
          showConfirmButton: false,
          showClose: false,
        })
      }
      let uploadStartTime = Date.now()
      Promise.all(validFileList.map(file => {
        return uploadFile(file, { appType: 'NLP' },     ({ loaded, total }) => {
          const progress = ~~((loaded / total) * 100)
          const fInfo = fileMap.get(file)
          if(fInfo) {
            const d = document.getElementById(fInfo.id)
            d && (d.innerText = `${progress}%` )
          }
        })
          .then(({ data }) => {
            uploadedFiles.value.push(data)
            if (selectedFiles.value.length < 3) {
              toggleRowSelect(data, true) // 上传文件后默认在表格中选中该行
            }
          })
          .catch((error) => {
            uploadFailedFiles.push({
              fileName:file.name,
              errorMsg: error.message,
            })
          })
      })).then(async () => {
        btnLoading.import = false
        if (Date.now() - uploadStartTime < 1000) {
          // 上传速度较快, 延迟关闭进度提示窗, 避免一闪而过
          await new Promise(res => setTimeout(res, 300))
        }
        MessageBox.close()
        if (uploadFailedFiles.length > 0) {
          MessageBox({
            title: '提示',
            message: h?.('div', undefined, [
              h?.('div', undefined, '部分文件上传失败： '),
              h?.('ol', undefined, uploadFailedFiles
                .map(({ fileName, errorMsg }) => h?.(
                  'li',
                  undefined,
                  [h?.('span', undefined, `${fileName}: ${errorMsg}`)])))
            ]),
            type: 'info',
            // 以下为满足需求: 导入中用户不可对父页面进行操作
          })
          uploadFailedFiles.length = 0
        }
      })

      if (uploadInputRef.value) {
        uploadInputRef.value.value = '' // 清空 input 的 value, 否则重复选择同一个文件不会触发 change 事件
      }
    }

    return {
      btnLoading,

      uploadInputRef,
      handleImportBtnClick,
      handleUploadInputChange,

      startRead,

      elTableRef,
      uploadedFiles,
      selectedFiles,
      checkIsRowSelectable,
      onTableSelectChange,
      deleteFiles,

      showReading,
    }
  },
})
